import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type WorkspaceAgentRunFinishedEvent,
  type WorkspaceAgentRunRecord,
  type WorkspaceAgentRunStartedEvent,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import {
  applyCancelActiveRun,
  applyLinkActiveRunToUserMessage,
  applyRunFinished,
  applyRunStarted,
  EMPTY_PRIVATE_WORKSPACE_AGENT_RUN_STATE,
  type LinkActiveRunToUserMessageOptions,
  resetPrivateWorkspaceAgentRunState,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/reducePrivateWorkspaceAgentRunState';

export type UsePrivateWorkspaceAgentRunStateParams = {
  getActiveSessionId: () => string | null;
};

export type UsePrivateWorkspaceAgentRunStateResult = {
  activeRunId: string | null;
  getRunsById: () => Record<string, WorkspaceAgentRunRecord>;
  handleCancelSucceeded: () => void;
  handleRunFinished: (event: WorkspaceAgentRunFinishedEvent) => void;
  handleRunStarted: (event: WorkspaceAgentRunStartedEvent) => void;
  linkActiveRunToUserMessage: (userMessageId: string, options?: LinkActiveRunToUserMessageOptions) => void;
  resetRunState: () => void;
  runsById: Record<string, WorkspaceAgentRunRecord>;
};

/** Tracks live agent runs announced over WSS for the open session. */
export const usePrivateWorkspaceAgentRunState = ({
  getActiveSessionId,
}: UsePrivateWorkspaceAgentRunStateParams): UsePrivateWorkspaceAgentRunStateResult => {
  const [runState, setRunState] = useState(EMPTY_PRIVATE_WORKSPACE_AGENT_RUN_STATE);
  const runsByIdRef = useRef(runState.runsById);
  const getRunsById = useCallback(() => runsByIdRef.current, []);

  useEffect(() => {
    runsByIdRef.current = runState.runsById;
  }, [runState.runsById]);

  const resetRunState = useCallback(() => {
    setRunState(resetPrivateWorkspaceAgentRunState());
  }, []);

  const handleRunStarted = useCallback(
    (event: WorkspaceAgentRunStartedEvent) => {
      if (event.sessionId !== getActiveSessionId()) {
        return;
      }

      setRunState((current) => applyRunStarted(current, event));
    },
    [getActiveSessionId],
  );

  const linkActiveRunToUserMessage = useCallback(
    (userMessageId: string, options?: LinkActiveRunToUserMessageOptions) => {
      setRunState((current) => applyLinkActiveRunToUserMessage(current, userMessageId, options));
    },
    [],
  );

  const handleRunFinished = useCallback(
    (event: WorkspaceAgentRunFinishedEvent) => {
      if (event.sessionId !== getActiveSessionId()) {
        return;
      }

      setRunState((current) => applyRunFinished(current, event));
    },
    [getActiveSessionId],
  );

  const handleCancelSucceeded = useCallback(() => {
    setRunState((current) => applyCancelActiveRun(current));
  }, []);

  return useMemo(
    () => ({
      activeRunId: runState.activeRunId,
      getRunsById,
      handleCancelSucceeded,
      handleRunFinished,
      handleRunStarted,
      linkActiveRunToUserMessage,
      resetRunState,
      runsById: runState.runsById,
    }),
    [
      getRunsById,
      handleCancelSucceeded,
      handleRunFinished,
      handleRunStarted,
      linkActiveRunToUserMessage,
      resetRunState,
      runState,
    ],
  );
};
